<template>
    <custom-table
        :title="$t('pages.ecommerce.checkout.title')"
        :subTitle="$t('pages.ecommerce.checkout.subTitle')"
        rowKey="id"
        :items="table.data"
        :loading="table.loading"
        selectableRows
        :columns="fields"
        :actions="actions"
        :pagination="table.pagination"
        :sortMeta="table.pagination.sort || []"
        changeUrlPage
        filterComponentName="CheckoutFilter"
        @action="handleClickAction"
        @selectableRows="handleSelectedRow"
        @changeTable="handleTableChange"
        @sort="handleSort"
    >
        <template v-slot:customer="{ row: record }">
            <div class="d-flex flex-column">
                <span class="text-muted fs-8">{{ $t('pages.ecommerce.checkout.orderable_types.' + record.orderable_type) }}</span>
                <span class="fw-bold">{{ formatOrderableName(record) }}</span>
            </div>
        </template>
        <template v-slot:order_type="{ row: record }">
            <span :class="orderTypeBadgeClass(record.order_type_id)" class="badge">{{ orderTypeLabel(record.order_type_id) }}</span>
        </template>
        <template v-slot:status="{ row: record }">
            <span :class="statusBadgeClass(record.status_id)" class="badge">{{ statusLabel(record.status_id) }}</span>
        </template>
        <template v-slot:final_amount="{ row: record }">
            {{ formatPrice(record.final_amount) }}
            <span class="text-muted">{{ record.currency?.code }}</span>
        </template>
        <template v-slot:paid_at="{ row: record }">
            {{ record.paid_at ? $moment(record.paid_at).format("DD.MM.YYYY HH:mm") : '-' }}
        </template>
        <template v-slot:created_at="{ row: record }">
            {{ record.created_at ? $moment(record.created_at).format("DD.MM.YYYY HH:mm") : '-' }}
        </template>
        <template v-slot:actions="{ row: record }">
            <div class="d-flex justify-content-end">
                <el-tooltip :content="$t('common.edit')" placement="top">
                    <router-link :to="'/ecommerce/checkout/save/' + record.id" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/art/art005.svg"/>
                        </span>
                    </router-link>
                </el-tooltip>
                <a @click="deleteRecord([record.id])" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                    <span class="svg-icon svg-icon-3">
                        <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                    </span>
                </a>
            </div>
        </template>
    </custom-table>
</template>

<script>
import CustomTable from "@/components/custom-table";

const STATUS_CODE_MAP = {
    0: 'PENDING', 1: 'AWAITING_PAYMENT', 2: 'PAID', 3: 'FAILED',
    4: 'CANCELLED', 5: 'REFUNDED', 6: 'PARTIALLY_PAID', 7: 'REFUND_REQUESTED',
};
const STATUS_BADGE_MAP = {
    0: 'badge-light-warning',
    1: 'badge-light-warning',
    2: 'badge-light-success',
    3: 'badge-light-danger',
    4: 'badge-light-dark',
    5: 'badge-light-info',
    6: 'badge-light-warning',
    7: 'badge-light-info',
};
const ORDER_TYPE_CODE_MAP = {1: 'PURCHASE', 2: 'RENEWAL', 3: 'UPGRADE'};
const ORDER_TYPE_BADGE_MAP = {
    1: 'badge-light-primary',
    2: 'badge-light-info',
    3: 'badge-light-success',
};

export default {
    name: "CheckoutIndex",
    components: {CustomTable},
    data() {
        return {
            fields: [
                {name: this.$t("pages.ecommerce.checkout.cols.id"), key: "id", sortable: true},
                {name: this.$t("pages.ecommerce.checkout.cols.customer"), scopedSlots: {customRender: "customer"}},
                {name: this.$t("pages.ecommerce.checkout.cols.order_type"), sortingField: "order_type_id", sortable: true, scopedSlots: {customRender: "order_type"}},
                {name: this.$t("pages.ecommerce.checkout.cols.final_amount"), sortingField: "final_amount", sortable: true, scopedSlots: {customRender: "final_amount"}},
                {name: this.$t("pages.ecommerce.checkout.cols.status"), sortingField: "status_id", sortable: true, scopedSlots: {customRender: "status"}},
                {name: this.$t("pages.ecommerce.checkout.cols.paid_at"), sortingField: "paid_at", sortable: true, scopedSlots: {customRender: "paid_at"}},
                {name: this.$t("pages.ecommerce.checkout.cols.created_at"), sortingField: "created_at", sortable: true, scopedSlots: {customRender: "created_at"}},
                {name: this.$t("common.action"), key: "action", scopedSlots: {customRender: "actions"}},
            ],
            actions: [
                {name: "new", icon: "bi-plus-lg", label: "btn.add"},
                {name: "refresh", icon: "bi-arrow-repeat", label: "btn.refresh"},
                {name: "delete", icon: "bi-trash", label: "btn.delete"},
            ],
            selectedRowKeys: [],
            filterParameters: {},
        }
    },
    computed: {
        table() {
            return this.$store.state.ecommerce.checkout.table;
        },
    },
    mounted() {
        this.setCurrentPageBreadcrumbs(this.$t("menu.checkoutList"), []);

        this.filterParameters = {
            sort: 'created_at:desc',
        };

        this.$store.dispatch('ecommerce/checkout/get', {
            page: {current: this.$route.query.page},
            filterData: Object.assign(this.filterParameters, this.$root.getFilterWithUrl({})),
        });
    },
    methods: {
        statusLabel(statusId) {
            return this.$t('pages.ecommerce.checkout.statuses.' + (STATUS_CODE_MAP[statusId] || 'PENDING'));
        },
        statusBadgeClass(statusId) {
            return STATUS_BADGE_MAP[statusId] || 'badge-secondary';
        },
        orderTypeLabel(typeId) {
            return this.$t('pages.ecommerce.checkout.order_types.' + (ORDER_TYPE_CODE_MAP[typeId] || 'PURCHASE'));
        },
        orderTypeBadgeClass(typeId) {
            return ORDER_TYPE_BADGE_MAP[typeId] || 'badge-light-primary';
        },
        formatPrice(price) {
            const value = parseFloat(price);
            if (isNaN(value)) return price;
            return value.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        },
        formatOrderableName(record) {
            const o = record.orderable;
            if (!o) return '#' + record.orderable_id;
            if (record.orderable_type === 'COMPANY') {
                return o.name || o.short_name || ('#' + o.id);
            }
            const fn = o.firstname || '';
            const ln = o.lastname || '';
            return (fn + ' ' + ln).trim() || o.email || ('#' + o.id);
        },
        handleClickAction(name) {
            switch (name) {
                case "refresh":
                    this.refreshTable();
                    break;
                case "new":
                    this.$router.push({path: "/ecommerce/checkout/save"});
                    break;
                case "delete":
                    this.deleteRecord(this.selectedRowKeys.flat());
                    break;
            }
        },
        refreshTable() {
            this.$store.dispatch('ecommerce/checkout/get', {
                page: {current: this.$route.query.page},
                filterData: Object.assign(this.filterParameters, this.$root.getFilterWithUrl({})),
            });
        },
        handleTableChange(pagination, filterData) {
            const currentSort = this.filterParameters.sort || 'created_at:desc';
            this.filterParameters = {...filterData, sort: currentSort};

            this.$store.dispatch("ecommerce/checkout/get", {
                page: pagination,
                filterData: this.filterParameters,
            });
        },
        handleSort({columnName, order}) {
            this.filterParameters = {
                ...this.filterParameters,
                sort: `${columnName}:${order}`,
            };

            this.$store.dispatch("ecommerce/checkout/get", {
                page: {current: this.$route.query.page},
                filterData: this.filterParameters,
            });
        },
        handleSelectedRow(record) {
            this.selectedRowKeys = record;
        },
        deleteRecord(id) {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('btn.delete'),
                message: this.$t('messages.sureDelete'),
                confirmType: 'danger',
                onConfirm: () => {
                    this.$store.dispatch("ecommerce/checkout/delete", {id}).then((successDeleted) => {
                        this.selectedRowKeys = this.selectedRowKeys.filter(x => !successDeleted.includes(x));
                        this.refreshTable();
                    });
                },
            });
        },
    }
}
</script>
